import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Slider,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import DashboardLayout from './DashboardLayout';
import * as api from '../utils/api';

const INSTRUCTION_TEMPLATES = [
  {
    name: 'Contact extraction',
    description: 'Extracts emails and phone numbers from a dialog',
    instruction: `Analyze the dialog and extract all contact details.

Return the result in JSON format:
{
  "emails": ["email1@example.com", "email2@example.com"],
  "phones": ["+12125551234", "+12025550123"],
  "social_media": ["@username", "facebook.com/profile"],
  "summary": "brief description of the found contacts"
}`
  },
  {
    name: 'Sentiment analysis',
    description: 'Determines the emotional tone of the dialog',
    instruction: `Analyze the dialog sentiment and determine the client attitude.

Return the result in JSON format:
{
  "sentiment": "positive|negative|neutral",
  "confidence": 0.95,
  "key_phrases": ["great product", "very satisfied"],
  "summary": "the client expresses a positive attitude towards the product"
}`
  },
  {
    name: 'Interest detection',
    description: 'Finds mentions of client interests and needs',
    instruction: `Find mentions of interests, needs, and preferences in the dialog.

Return the result in JSON format:
{
  "interests": ["sports", "technology", "travel"],
  "needs": ["new phone", "insurance"],
  "budget": "$200-500",
  "timeline": "within a month",
  "summary": "client profile description"
}`
  }
];

const steps = [
  'Basic information',
  'Choose campaign',
  'Analysis settings',
  'Preview and create'
];

const validationSchema = yup.object({
  name: yup.string().required('Task name is required'),
  campaign_id: yup.number().required('Please select a campaign'),
  instructions: yup.string().required('AI instructions are required'),
  max_dialog_duration_hours: yup.number().min(1, 'Minimum 1 hour').max(168, 'Maximum 168 hours (1 week)').required('Specify dialog duration')
});

const CreateAnalysisTask = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues: { name: '', description: '', campaign_id: '', instructions: '', max_dialog_duration_hours: 24 },
    validationSchema,
    onSubmit: async (values) => { await handleCreateTask(values); }
  });

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setCampaignsLoading(true);
        const response = await api.getCompletedCampaigns();
        const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setCampaigns(list);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load campaigns');
      } finally { setCampaignsLoading(false); }
    };
    fetchCampaigns();
  }, []);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handlePreview = async () => {
    if (!formik.values.campaign_id || !formik.values.max_dialog_duration_hours) return;
    try {
      setLoading(true);
      const response = await api.previewAnalysisTask({ campaign_id: formik.values.campaign_id, max_dialog_duration_hours: formik.values.max_dialog_duration_hours });
      setPreview(response.data.data);
    } catch (err) {
      console.error('Error previewing task:', err);
      setError('Failed to preview task');
    } finally { setLoading(false); }
  };

  const handleCreateTask = async (values) => {
    try {
      setLoading(true);
      await api.createAnalysisTask(values);
      navigate('/campaign-analysis');
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.response?.data?.message || 'Failed to create task');
    } finally { setLoading(false); }
  };

  const applyTemplate = (template) => { formik.setFieldValue('instructions', template.instruction); };

  const canProceedToNextStep = (step) => {
    switch (step) {
      case 0: return formik.values.name && !formik.errors.name;
      case 1: return formik.values.campaign_id && !formik.errors.campaign_id;
      case 2: return formik.values.instructions && !formik.errors.instructions;
      default: return true;
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaign-analysis')} sx={{ mb: 2 }}>Back to task list</Button>
        <Typography variant="h4" gutterBottom>Create campaign analysis task</Typography>
        <Typography variant="body1" color="text.secondary">Create a task to analyze a completed campaign using AI</Typography>
      </Box>

      {error && (<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>)}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>{steps[0]}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <TextField fullWidth label="Task name" name="name" value={formik.values.name} onChange={formik.handleChange} error={formik.touched.name && Boolean(formik.errors.name)} helperText={formik.touched.name && formik.errors.name} margin="normal" required />
                <TextField fullWidth label="Description (optional)" name="description" value={formik.values.description} onChange={formik.handleChange} multiline rows={3} margin="normal" />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Button variant="contained" onClick={handleNext} disabled={!canProceedToNextStep(0)}>Continue</Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[1]}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {campaignsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography>Loading campaigns...</Typography>
                  </Box>
                ) : (
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select a completed campaign</InputLabel>
                    <Select name="campaign_id" value={formik.values.campaign_id} onChange={formik.handleChange} error={formik.touched.campaign_id && Boolean(formik.errors.campaign_id)}>
                      {campaigns.map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          <Box>
                            <Typography variant="subtitle2">{campaign.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : 'N/A'} - {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.campaign_id && formik.errors.campaign_id && (
                      <Typography variant="caption" color="error">{formik.errors.campaign_id}</Typography>
                    )}
                  </FormControl>
                )}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                <Button variant="contained" onClick={handleNext} disabled={!canProceedToNextStep(1) || campaignsLoading}>Continue</Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[2]}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Maximum dialog duration: {formik.values.max_dialog_duration_hours} hours</Typography>
                <Slider name="max_dialog_duration_hours" value={formik.values.max_dialog_duration_hours} onChange={(e, value) => formik.setFieldValue('max_dialog_duration_hours', value)} min={1} max={168} step={1} marks={[{ value: 12, label: '12h' }, { value: 24, label: '1 day' }, { value: 72, label: '3 days' }, { value: 168, label: '7 days' }]} sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>Instruction templates</Typography>
                <Box sx={{ mb: 2 }}>
                  {INSTRUCTION_TEMPLATES.map((template, index) => (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle1">{template.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{template.description}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', p: 2, borderRadius: 1, whiteSpace: 'pre-wrap' }}>{template.instruction}</Typography>
                        </Box>
                        <Button variant="outlined" size="small" onClick={() => applyTemplate(template)}>Apply template</Button>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
                <TextField fullWidth label="AI instructions" name="instructions" value={formik.values.instructions} onChange={formik.handleChange} error={formik.touched.instructions && Boolean(formik.errors.instructions)} helperText={formik.touched.instructions && formik.errors.instructions} multiline rows={8} margin="normal" required placeholder="Describe what the AI should do with each dialog..." />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                <Button variant="contained" onClick={handleNext} disabled={!canProceedToNextStep(2)}>Continue</Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[3]}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Task summary</Typography>
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography><strong>Name:</strong> {formik.values.name}</Typography>
                  {formik.values.description && (<Typography><strong>Description:</strong> {formik.values.description}</Typography>)}
                  <Typography><strong>Campaign:</strong> {campaigns.find(c => c.id === formik.values.campaign_id)?.name}</Typography>
                  <Typography><strong>Dialog duration:</strong> {formik.values.max_dialog_duration_hours} hours</Typography>
                </Box>
                <Button variant="outlined" startIcon={<PreviewIcon />} onClick={handlePreview} disabled={loading} sx={{ mb: 2 }}>
                  {loading ? 'Calculating...' : 'Estimate dialogs'}
                </Button>
                {preview && (
                  <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>Processing preview</Typography>
                    <Typography><strong>Dialogs to analyze:</strong> {preview.estimated_dialogs}</Typography>
                      <Typography><strong>Stats:</strong>
                          <Chip label={preview.estimated_dialogs} color="success" size="small"/> / <Chip label={preview.estimated_noreply} color="error" size="small"/> / <Chip label={preview.estimated_total} color="primary" size="small"/>
                      </Typography>
                    <Typography gutterBottom sx={{ mt: 1 }}><strong>Campaign instances:</strong></Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {(preview.campaign_instances || []).map((instance, index) => (
                        <Chip key={index} label={`${instance.alias || ''} (${instance.phone || ''})`} size="small" />
                      ))}
                    </Box>
                    {preview.eligible_chats && preview.eligible_chats.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Eligible chats for analysis ({preview.eligible_chats.length}):</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Only chats with &gt;4 total messages and ≥3 outgoing messages will be analyzed
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
                          {preview.eligible_chats.map((chat, index) => (
                            <Box key={index} sx={{ mb: 1, pb: 1, borderBottom: index < preview.eligible_chats.length - 1 ? 1 : 0, borderColor: 'grey.200' }}>
                              <Typography variant="body2">
                                <strong>{chat.contact_name || chat.contact_phone}</strong> ({chat.contact_phone})
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Messages: {chat.total_messages} (Out: {chat.outgoing_messages}, In: {chat.incoming_messages}) | 
                                Instance: {chat.instance_phone}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                <Button variant="contained" onClick={formik.handleSubmit} disabled={loading || !formik.isValid}>
                  {loading ? 'Creating...' : 'Create task'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </DashboardLayout>
  );
};

export default CreateAnalysisTask;
